import { ReactNode } from 'react';
import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { AppCard, PageContainer } from '../components/ui';
import { IconBadgeTone, iconBadgeTones } from '../themes';

interface ILayoutBaseDePaginaProps {
    saldoanterior: string;
    credito: string;
    debito: string;
    saldo: string;
    saldoreal: string;
    children: React.ReactNode
    barraDeFerramentas?: ReactNode;
    aoClicarEmSaldoReal?: () => void;
    // Antes da primeira pesquisa não há conta selecionada, então os KPIs por
    // conta (saldo anterior/crédito/débito/saldo/saldo real) não fazem
    // sentido ainda — nesse momento a linha mostra os 4 totais gerais
    // (recebíveis/cofre/caixa/contas bancárias) abaixo. Depois da primeira
    // pesquisa volta a mostrar os KPIs da conta, como sempre foi.
    exibirTotaisGerais?: boolean;
    totalRecebiveis?: string;
    totalCofre?: string;
    totalCaixa?: string;
    totalContasBancarias?: string;
}

// 4 KPIs (totais gerais) numa linha só a partir de sm (3 * 4 = 12 colunas);
// 2-a-2 no celular.
const gridKpi4 = { xs: 6, sm: 3 } as const;
// 5 KPIs (por conta) numa linha só a partir de md (2.4 * 5 = 12 colunas);
// 2-a-2 no celular, 3 numa fileira ímpar em telas intermediárias.
const gridKpi5 = { xs: 6, sm: 4, md: 2.4 } as const;

interface IStatCardProps {
  tone: IconBadgeTone;
  nome: string;
  valor: string;
  onClick?: () => void;
}

// Card financeiro compacto — faixa colorida por tom à esquerda + rótulo/valor,
// ~80-95px de altura, sem ícone (layout de referência: 4 cards lado a lado
// com barra de cor à esquerda, "SALDO ANTERIOR"/"CRÉDITO"/"DÉBITO"/"SALDO
// REAL"). A cor aparece só na faixa, não no card inteiro.
const StatCard: React.FC<IStatCardProps> = ({ tone, nome, valor, onClick }) => (
  <AppCard interactive={!!onClick} onClick={onClick} sx={{ height: '100%', minHeight: 88, position: 'relative', overflow: 'hidden' }}>
    <Box sx={{ position: 'absolute', insetBlock: 0, insetInlineStart: 0, width: 4, backgroundColor: iconBadgeTones[tone].iconColor }} />
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', py: 1.75, pl: 2.5, pr: 1.75 }}>
      <Typography
        variant="caption"
        noWrap
        sx={{ display: 'block', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, fontSize: '0.7rem' }}
      >
        {nome}
      </Typography>
      <Typography noWrap sx={{ fontWeight: 650, fontSize: '1.125rem', mt: 0.5 }}>
        {valor}
      </Typography>
    </Box>
  </AppCard>
);

export const LayoutBaseDeFinanceiroConciliacao: React.FC<ILayoutBaseDePaginaProps> = ({
  children,
  saldoanterior,
  credito,
  debito,
  saldo,
  saldoreal,
  barraDeFerramentas,
  aoClicarEmSaldoReal,
  exibirTotaisGerais,
  totalRecebiveis,
  totalCofre,
  totalCaixa,
  totalContasBancarias,
}) => {
  return (
    <PageContainer disablePadding sx={{ gap: 1.5 }}>
      <Box px={2} pt={1.5}>
        <Grid container spacing={1.25}>
          {exibirTotaisGerais ? (
            <>
              <Grid item {...gridKpi4}>
                <StatCard tone="primary" nome="Total em Recebíveis" valor={totalRecebiveis ?? '0,00'} />
              </Grid>
              <Grid item {...gridKpi4}>
                <StatCard tone="success" nome="Total no Cofre" valor={totalCofre ?? '0,00'} />
              </Grid>
              <Grid item {...gridKpi4}>
                <StatCard tone="error" nome="Total em Caixa" valor={totalCaixa ?? '0,00'} />
              </Grid>
              <Grid item {...gridKpi4}>
                <StatCard tone="purple" nome="Total nas Contas Bancárias" valor={totalContasBancarias ?? '0,00'} />
              </Grid>
            </>
          ) : (
            <>
              <Grid item {...gridKpi5}>
                <StatCard tone="primary" nome="Saldo Anterior" valor={saldoanterior} />
              </Grid>
              <Grid item {...gridKpi5}>
                <StatCard tone="success" nome="Crédito" valor={credito} />
              </Grid>
              <Grid item {...gridKpi5}>
                <StatCard tone="error" nome="Débito" valor={debito} />
              </Grid>
              <Grid item {...gridKpi5}>
                <StatCard tone="cyan" nome="Saldo" valor={saldo} />
              </Grid>
              <Grid item {...gridKpi5}>
                <StatCard tone="purple" nome="Saldo Real" valor={saldoreal} onClick={aoClicarEmSaldoReal} />
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {barraDeFerramentas && (
        <Box>
          {barraDeFerramentas}
        </Box>
      )}

      <Box padding={1} overflow="auto">
        {children}
      </Box>
    </PageContainer>
  );
};
